<template>
  <div class="relative">
    <div
      role="button"
      class="custom-shadow relative overflow-hidden rounded-lg"
      @click="pickerIsActive = !pickerIsActive"
    >
      <input
        v-if="range"
        :value="
          Object.keys(value).length
            ? `${moment(value.start).format(dateFormatting)} - ${moment(
                value.end
              ).format(dateFormatting)}`
            : undefined
        "
        placeholder="Selecione a data"
        class="bg-ontick-graphite-500 pointer-events-none h-10 w-full cursor-pointer px-2 text-sm"
        disabled
      />
      <input
        v-else
        :value="
          Object.keys(value).length
            ? moment(value.start).format(dateFormatting)
            : undefined
        "
        placeholder="Selecione a data"
        class="bg-ontick-graphite-500 pointer-events-none h-10 w-full cursor-pointer px-2 text-sm"
        disabled
      />
      <button
        class="bg-ontick-graphite-500 absolute top-1/2 right-0 h-full -translate-y-1/2 transform px-4"
      >
        <i v-html="$icons.calendar"></i>
      </button>
    </div>
    <div
      v-if="pickerIsActive"
      class="custom-shadow bg-ontick-graphite-500 absolute top-12 flex max-h-[382px] w-min items-start gap-4 rounded-2xl p-4 text-sm shadow-lg"
    >
      <shortcuts-component
        v-if="range && enableShortcuts"
        v-bind="shortcutProps"
        @set-range="selectedDate = $event"
        @previous-month="shortcutPreviousMonth"
        @previous-year="shortcutPreviousYear"
        @current-month="shortcutCurrentMonth"
        @current-year="shortcutCurrentYear"
      />
      <div class="flex w-[280px] flex-col gap-4">
        <header-component
          :activePicker="activePicker"
          :displayedDate="displayedDate"
          @previous="selectPreviousMonth"
          @next="selectNextMonth"
          @toggle-date-selector="toggleDateSelector"
          @toggle-month-selector="toggleMonthSelector"
          @toggle-year-selector="toggleYearSelector"
        />
        <calendar-component
          v-if="activePicker === 'date'"
          :displayedDate="displayedDate"
          :selectedDate="selectedDate"
          v-bind="shortcutProps"
          @select-date="select"
        />
        <month-picker-component
          v-else-if="activePicker === 'month'"
          :selectedMonth="selectedMonth"
          @select-month="selectMonth"
        />
        <year-picker-component
          v-else-if="activePicker === 'year'"
          :selectedYear="selectedYear"
          @select-year="selectYear"
        />
        <div
          class="flex w-full items-end"
          :class="type === 'date' ? 'justify-end' : 'justify-between'"
        >
          <hour-picker-component
            v-if="type === 'datetime' && !range"
            v-model="time"
          />
          <button
            @click="publishDate"
            class="py-.5 bg-ontick-green-600 rounded-lg px-4 filter transition-all hover:brightness-125"
          >
            <i v-html="$icons.check"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HeaderComponent from "./header.component.vue";
import CalendarComponent from "./calendar.component.vue";
import HourPickerComponent from "./hour-picker.component.vue";
import MonthPickerComponent from "./month-picker.component.vue";
import MonthYearPickerComponent from "./month-year-picker.component.vue";
import shortcutsComponent from "./shortcuts.component.vue";
import YearPickerComponent from "./year-picker.component.vue";
import ClickOutside from "vue-click-outside";
export default {
  components: {
    shortcutsComponent,
    MonthYearPickerComponent,
    CalendarComponent,
    HourPickerComponent,
    MonthPickerComponent,
    YearPickerComponent,
    HeaderComponent,
  },

  directives: {
    ClickOutside,
  },

  props: {
    range: {
      type: Boolean,
    },
    allowPast: {
      type: Boolean,
    },
    allowFuture: {
      type: Boolean,
      default: true,
    },
    allowWeekends: {
      type: Boolean,
    },
    showAdjacentMonthDays: {
      type: Boolean,
    },
    value: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      default: "date",
      validator: (type) => {
        return ["date", "datetime"].includes(type);
      },
    },
    enableShortcuts: {
      type: Boolean,
    },
  },

  data() {
    return {
      displayedDate: this.moment().startOf("month"),
      activePicker: "date",
      dateToSet: "start",
      selectedDate: {},
      pickerIsActive: false,
      time: {
        hour: this.moment().hour(),
        minute: this.moment().minute(),
      },
    };
  },

  computed: {
    shortcutProps() {
      return {
        range: this.range,
        allowFuture: this.allowFuture,
        allowPast: this.allowPast,
        allowWeenkends: this.allowWeekends,
      };
    },
    selectedMonth() {
      return this.moment(this.displayedDate).month();
    },
    selectedYear() {
      return this.moment(this.displayedDate).year();
    },
    dateFormatting() {
      switch (this.type) {
        case "date":
          return "DD MMM YYYY";
        case "datetime":
          return "DD MMM YYYY HH:mm";
        default:
          return "DD MMM YYYY";
      }
    },
  },

  methods: {
    shortcutCurrentMonth() {
      this.displayedDate = this.moment();
    },
    shortcutCurrentYear() {
      this.displayedDate = this.moment(this.displayedDate).year(
        this.moment().year()
      );
    },
    selectPreviousMonth() {
      this.displayedDate = this.moment(this.displayedDate).subtract(1, "month");
    },
    shortcutPreviousMonth() {
      if (this.moment(this.displayedDate).month() === this.moment().month()) {
        this.displayedDate = this.moment(this.displayedDate).subtract(
          1,
          "month"
        );
      }
    },
    selectNextMonth() {
      this.displayedDate = this.moment(this.displayedDate).add(1, "month");
    },
    shortcutPreviousYear() {
      if (this.moment(this.displayedDate).year() === this.moment().year()) {
        this.displayedDate = this.moment(this.displayedDate)
          .subtract(1, "year")
          .endOf("year");
      }
    },
    toggleDateSelector() {
      this.activePicker = "date";
    },
    toggleMonthSelector() {
      this.activePicker = "month";
    },
    toggleYearSelector() {
      this.activePicker = "year";
    },
    selectMonth(number) {
      this.displayedDate = this.moment(this.displayedDate).month(number);
      this.activePicker = "date";
    },
    selectYear(year) {
      this.displayedDate = this.moment(this.displayedDate).year(year);
      this.activePicker = "date";
    },
    selectDate(date) {
      this.selectedDate = {
        start: this.moment(date).startOf("day"),
        end: this.moment(date).endOf("day"),
      };
    },
    selectRange(date) {
      if (this.dateToSet === "start") {
        this.selectedDate = {
          start: this.moment(date).startOf("day"),
        };
        this.dateToSet = "end";
      } else if (this.dateToSet === "end") {
        this.selectedDate = {
          ...this.selectedDate,
          end: this.moment(date).endOf("day"),
        };
        this.dateToSet = "start";
      }
    },
    select(date) {
      if (this.range) {
        this.selectRange(date);
      } else {
        this.selectDate(date);
      }
    },
    publishDate() {
      if (this.type === "datetime" && !this.range) {
        this.selectedDate.start = this.moment(this.selectedDate.start).hour(
          this.time.hour
        );
        this.selectedDate.end = this.moment(this.selectedDate.end).hour(
          this.time.hour
        );
        this.selectedDate.start = this.moment(this.selectedDate.start).minute(
          this.time.minute
        );
        this.selectedDate.end = this.moment(this.selectedDate.end).minute(
          this.time.minute
        );
      }
      this.$emit("input", this.selectedDate);
      this.pickerIsActive = false;
    },
  },

  mounted() {
    if (this.range && this.type === "datetime") {
      console.warn(
        `Tipo "datetime" é inválido quando "range === true" e o componente poderá aprensentar problemas.`
      );
    }
  },
};
</script>
<style lang="scss" scoped>
.custom-shadow {
  box-shadow: 0px 2px 4px 1px rgba(0, 0, 0, 0.35);
}
</style>
